import { useEffect, useState } from 'react';
import { ScorecardData, ScorecardLicenseSearchRequest, ScorecardSearchRequest } from '../../Component/Model/Dto/ScorecardLicense';
import { defaultObject } from '../../Component/Model/helpers';
import { EntriesPerPage } from '../../Component/Model/Table/Table';
import { ScorecardLicense } from './ScorecardBasePage/ScorecardLicense';
import { ScorecardTableMapper } from './ScorecardBasePage/ScorecardLicenseTableColumn';
import { Grid } from '@mui/material';
import { useResetRecoilState } from 'recoil';
import { ScorecardAtom } from '../../State/Scorecard.Atom';
import { EEScorecardAtom } from '../../State/EEScorecard.Atom';
import { ColumnSortOption, SortDirections } from '../../Component/Model/Dto/ColumnSortOption';
import { ConfirmationDialog } from '../../Component/Common/Components/ConfirmationDialog';
import { toast } from 'react-toastify';
import { BEEScorecardService } from '../../Component/Api/BEEScorecard/BEEScorecard';
import { ConsolidationAtom } from '../../State/Consolidation.Atom';
import { EE123InterestModal } from './EE123InterestModal';
import { useHistory } from 'react-router-dom';
import { WSPScorecardAtom } from '../../State/WSPScorecard.Atom';
import { OrganisationAtom } from '../../State/Organisation.Atom';
import { BBBEEScorecardAtom } from '../../State/BBBEEScorecard.Atom';
import { ClientRegistrationAtom } from '../../State/Advisory/ClientRegistration.Atom';
import { AssignModal } from '../BEEScorecards/AssignModal';

export const EmploymentEquityBase = () => {

    const history = useHistory();

    const [scorecardData, setScorecardData] = useState<ScorecardData[]>(defaultObject);
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
    const [filteredItemsCount, setFilteredItemsCount] = useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [searchValue, setSearchValue] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [sortColumnNames, setSortColumnNames] = useState<string[]>([]);
    const [sortDirections, setSortDirections] = useState<SortDirections[]>([]);
    const [showInterestModal, setShowInterestModal] = useState<boolean>(false);

    const resetScorecard = useResetRecoilState(ScorecardAtom);
    const resetScorecardData = useResetRecoilState(EEScorecardAtom);
    const resetScorecardType = useResetRecoilState(ConsolidationAtom);
    const resetWSPScorecard = useResetRecoilState(WSPScorecardAtom);
    const resetClientRegistration = useResetRecoilState(ClientRegistrationAtom);
    const resetOrganisation = useResetRecoilState(OrganisationAtom);
    const resetBBBEEScorecard = useResetRecoilState(BBBEEScorecardAtom);

    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
    const confirmDeleteMessage = "Are you sure you want to delete this scorecard?";
    const [showAssignModal, setShowAssignModal] = useState<boolean>(false);
    const [selectedScorecardId, setSelectedScorecardId] = useState<string>("");

    const scorecardLicenseSearchRequest: ScorecardLicenseSearchRequest = {
        Draw: 2,
        PageNumber: pageNumber,
        PageSize: entriesPerPage.defaultValue!,
        SortColumnNames: ['ScorecardTypeName'],
        SortDirections: ['Ascending'],
        SearchValue: "",
        ConsolidationValues: null,
        IsGroup: false,
        ScorecardTypes: ["43"]
    };

    const checkScorecardLicense = async () => {
        setIsLoading(true);
        try {
            const scorecardSummaryResult = await BEEScorecardService.getScorecardLicenseSummaryData(scorecardLicenseSearchRequest);
            if (scorecardSummaryResult.data.dataObject.length === 0) {
                setShowInterestModal(true);
            }

        } catch (error) {
            console.log(error);

        } finally {
            setIsLoading(false);
        }
    }


    const searchScorecardData = async () => {
        setIsLoading(true);
        try {
            const scorecardSearchResult = await BEEScorecardService.getUserScorecards(scorecardSearchRequest);
            setScorecardData(scorecardSearchResult.data.dataObject); //Set the API data, not the table data
            setFilteredItemsCount(scorecardSearchResult.data.filteredItemsCount);
            setTotalItemsCount(scorecardSearchResult.data.totalItemsCount);


        } catch (error) {
            console.log(error);
            setIsLoading(false);
            setScorecardData(defaultObject);
            setFilteredItemsCount(1);
            setTotalItemsCount(1);
        } finally {
            setIsLoading(false);
        }
    }

    const handleCloseConfirmation = () => {
        setShowConfirmationDialog(false);
        setSelectedScorecardId("");
    }
    
    const handleDelete = async () => {
        try {
            await BEEScorecardService.deleteScorecard(selectedScorecardId);
            toast.success("Successfully deleted scorecard");
            searchScorecardData();
        } catch (error) {
            console.log(error)
        } finally {
            handleCloseConfirmation();
        }
    }

    useEffect(() => {
        resetScorecard();
        resetScorecardData();
        resetScorecardType();
        searchScorecardData();
        checkScorecardLicense();
        resetWSPScorecard();
        resetClientRegistration();
        resetOrganisation()
        resetBBBEEScorecard();
    }, [entriesPerPage, pageNumber, searchValue, sortColumnNames, sortDirections]); // eslint-disable-line react-hooks/exhaustive-deps

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const searchValueChanged = (searchValue: string) => {
        setSearchValue(searchValue);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setSortColumnNames([sortOption.ColumnName]);
        setSortDirections([sortOption.SortDirection]);
        setPageNumber(1);
    }
    
    const handleAssignUser = (id: string) => {
        setSelectedScorecardId(id);
        setShowAssignModal(true);
    };
    
    const closeAssignUserModal = () => {
        setSelectedScorecardId("");
        setShowAssignModal(false);
    };
    
    const scorecardSearchRequest: ScorecardSearchRequest = {
        Draw: 2,
        PageNumber: pageNumber,
        PageSize: entriesPerPage.defaultValue!,
        SortColumnNames: sortColumnNames,
        SortDirections: sortDirections,
        SearchValue: searchValue,
        ConsolidationValues: null,
        IsGroupDataManagement: false,
        OnlySearchLockedScorecards: null,
        ScorecardEndDate: null,
        ScorecardTypes: [],
        Usernames: null,
        IsEEScorecardTable: true
    };

    const tableData = ScorecardTableMapper(scorecardData, handleAssignUser);
    
    return (
        <div className='center'>
            <Grid container>
                <Grid item xs={12}>
                    <ScorecardLicense
                        scorecardData={tableData}
                        entriesPerPage={entriesPerPage}
                        pageNumber={pageNumber}
                        filteredItemsCount={filteredItemsCount}
                        totalItemsCount={totalItemsCount}
                        entriesPerPageChanged={entriesPerPageChanged}
                        pageNumberChanged={pageNumberChanged}
                        searchValueChanged={searchValueChanged}
                        isLoading={isLoading}
                        columnSortingChanged={columnSortingChanged}
                    />
                    <ConfirmationDialog
                        showDialog={showConfirmationDialog}
                        handleAccept={handleDelete}
                        handleCancel={handleCloseConfirmation}
                        heading={"Confirm Delete"}
                        message={confirmDeleteMessage}
                    />
                    {showAssignModal && (
                        <AssignModal
                            id={selectedScorecardId}
                            showAssignModal={showAssignModal}
                            isOrganisationUserAccess={false}
                            isEEScorecard={true}
                            closeAssignModal={closeAssignUserModal}
                        />
                    )}
                </Grid>
            </Grid>
            
            <EE123InterestModal
                handleClose={() => {
                    setShowInterestModal(false);
                    history.push('/home');
                }}
                showInterestModal={showInterestModal}
            />
        </div>
    )
}

